import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plane, Plus, CalendarDays, Check, X, Undo2, User as UserIcon, Coffee, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { MemberSearchSelect } from "@/components/MemberSearchSelect";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const isoDate = (d) => d.toISOString().slice(0, 10);

const TYPES = [
  { v: "casual", label: "Casual" },
  { v: "sick", label: "Sick" },
  { v: "other", label: "Other" },
];
const TYPE_LABEL = { casual: "Casual", sick: "Sick", other: "Other" };
const STATUS = {
  pending: { label: "Pending", cls: "bg-amber-500/15 text-amber-600 border-amber-500/20" },
  approved: { label: "Approved", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/20" },
  rejected: { label: "Rejected", cls: "bg-destructive/15 text-destructive border-destructive/20" },
  withdrawn: { label: "Withdrawn", cls: "bg-foreground/10 text-muted-foreground border-border" },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.pending;
  return <Badge variant="outline" className={`rounded-full text-[11px] ${s.cls}`} data-testid={`leave-status-${status}`}>{s.label}</Badge>;
}

function RequestCard({ r, showRequester, actions }) {
  return (
    <Card className="p-4 rounded-xl" data-testid={`leave-card-${r.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          {showRequester && <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><UserIcon className="h-3.5 w-3.5 text-muted-foreground" />{r.user_name}</p>}
          <div className="text-sm mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="flex items-center gap-1 text-foreground"><CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />{r.start_date}{r.end_date !== r.start_date ? ` → ${r.end_date}` : ""}</span>
            <span className="text-xs text-muted-foreground">({r.days} day{r.days > 1 ? "s" : ""})</span>
            <Badge variant="secondary" className="rounded-full text-[10px]">{TYPE_LABEL[r.type] || "Other"}</Badge>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={r.status} />
          {r.withdraw_pending && <Badge variant="outline" className="rounded-full text-[10px] text-amber-600 border-amber-500/30" data-testid={`leave-withdraw-pending-${r.id}`}>Withdrawal pending</Badge>}
        </div>
      </div>
      {r.reason && <p className="text-sm text-foreground/80 mt-2 break-words">{r.reason}</p>}
      <p className="text-[11px] text-muted-foreground mt-2">
        {showRequester ? "Assigned to you" : `Approver: ${r.manager_name}`}
        {(r.viewer_names || []).length > 0 ? ` · Viewers: ${r.viewer_names.join(", ")}` : ""}
        {r.status !== "pending" && r.decided_by_name ? ` · ${r.status} by ${r.decided_by_name}` : ""}
      </p>
      {r.manager_note && <p className="text-xs mt-1.5 rounded-lg bg-secondary/50 p-2" data-testid={`leave-note-${r.id}`}><span className="text-muted-foreground">Note: </span>{r.manager_note}</p>}
      {actions}
    </Card>
  );
}

function ReviewActions({ r, kind, onDone, dir }) {
  const [note, setNote] = useState("");
  const [tagIds, setTagIds] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const isWithdrawal = kind === "withdrawal";
  const existing = new Set([r.user_id, r.manager_id, ...(r.viewer_ids || [])]);
  const tagOptions = (dir || []).filter((p) => !existing.has(p.id) && !tagIds.includes(p.id));
  const nameOf = (id) => (dir || []).find((p) => p.id === id)?.name || id;
  const decide = async (action) => {
    setBusy(true);
    try {
      const url = isWithdrawal ? `/leave/${r.id}/withdrawal-decision` : `/leave/${r.id}/decision`;
      const body = { action, note };
      if (!isWithdrawal && action === "approve" && tagIds.length) body.add_viewer_ids = tagIds;
      await api.post(url, body);
      toast.success(
        isWithdrawal
          ? (action === "approve" ? "Withdrawal approved — leave days reverted" : "Withdrawal rejected")
          : (action === "approve" ? "Leave approved" : "Leave rejected"));
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2 border-t border-border pt-3">
      {isWithdrawal && <p className="text-[11px] text-amber-600 font-medium">This member wants to withdraw an approved leave.</p>}
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note…" data-testid={`leave-note-input-${r.id}`} />
      {!isWithdrawal && (
        <div className="space-y-1.5">
          <Label className="text-[11px] text-muted-foreground">Also tag someone (optional — they can then view it)</Label>
          <MemberSearchSelect people={tagOptions} value={pick} onChange={(v) => { if (v) { setTagIds((p) => [...p, v]); setPick(""); } }} placeholder="Search a person to tag…" testid={`leave-tag-select-${r.id}`} />
          {tagIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid={`leave-tagged-${r.id}`}>
              {tagIds.map((id) => (
                <Badge key={id} variant="secondary" className="rounded-full text-[11px]">{nameOf(id)}<button className="ml-1" onClick={() => setTagIds((p) => p.filter((x) => x !== id))}><X className="h-3 w-3" /></button></Badge>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" className="rounded-lg flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => decide("approve")} data-testid={`leave-approve-${r.id}`}><Check className="h-4 w-4 mr-1" /> {isWithdrawal ? "Approve withdrawal" : "Approve"}</Button>
        <Button size="sm" variant="outline" className="rounded-lg flex-1 text-destructive border-destructive/30 hover:bg-destructive/10" disabled={busy} onClick={() => decide("reject")} data-testid={`leave-reject-${r.id}`}><X className="h-4 w-4 mr-1" /> Reject</Button>
      </div>
    </div>
  );
}

function MineActions({ r, onDone }) {
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const cancel = async () => {
    if (!(await confirm({ title: "Cancel this leave request?", description: "This pending request will be removed.", confirmText: "Cancel request", destructive: true }))) return;
    setBusy(true);
    try { await api.delete(`/leave/${r.id}`); toast.success("Request cancelled"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const withdraw = async () => {
    if (!(await confirm({ title: "Request withdrawal?", description: "Your approver must accept this withdrawal of the approved leave.", confirmText: "Request withdrawal" }))) return;
    setBusy(true);
    try { await api.post(`/leave/${r.id}/withdraw`); toast.success("Withdrawal requested"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  if (r.status === "pending") {
    return <Button variant="outline" size="sm" className="mt-3 rounded-lg text-destructive border-destructive/30 hover:bg-destructive/10" disabled={busy} onClick={cancel} data-testid={`leave-cancel-${r.id}`}><X className="h-4 w-4 mr-1" /> Cancel request</Button>;
  }
  if (r.status === "approved" && !r.withdraw_pending) {
    return <Button variant="outline" size="sm" className="mt-3 rounded-lg" disabled={busy} onClick={withdraw} data-testid={`leave-withdraw-${r.id}`}><Undo2 className="h-4 w-4 mr-1" /> Request withdrawal</Button>;
  }
  return null;
}

export default function LeaveView() {
  const { user } = useAuth();
  const [data, setData] = useState({ mine: [], to_review: [], viewing: [], team: [] });
  const [dir, setDir] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sub, setSub] = useState("requests");

  const isManager = user.role === "manager" || user.is_owner;
  const canRequest = !user.is_owner;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lv, d] = await Promise.all([api.get("/leave"), api.get("/directory")]);
      setData(lv.data);
      setDir(d.data);
    } catch (e) { toast.error("Could not load leave requests"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const needsAction = data.to_review.filter((r) => r.status === "pending" || (r.status === "approved" && r.withdraw_pending));
  const reviewed = data.to_review.filter((r) => !(r.status === "pending" || (r.status === "approved" && r.withdraw_pending)));

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Plane className="h-5 w-5" /></div>
        <div>
          <h2 className="font-heading text-xl font-bold">Leave requests</h2>
          <p className="text-sm text-muted-foreground">Raise a leave request and pick a manager to approve it.</p>
        </div>
        {canRequest && <Button className="ml-auto rounded-lg" onClick={() => setCreating(true)} data-testid="new-leave-btn"><Plus className="h-4 w-4 mr-1.5" /> New request</Button>}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <Tabs value={sub} onValueChange={setSub} className="w-full">
          <TabsList className="mb-5 flex-wrap h-auto" data-testid="leaveoff-subtabs">
            <TabsTrigger value="requests" data-testid="subtab-requests"><Plane className="h-4 w-4 mr-1.5" /> Requests</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="subtab-calendar"><CalendarDays className="h-4 w-4 mr-1.5" /> Calendar</TabsTrigger>
            {canRequest && <TabsTrigger value="myoff" data-testid="subtab-myoff"><Coffee className="h-4 w-4 mr-1.5" /> Next week Off</TabsTrigger>}
            {isManager && <TabsTrigger value="offbalance" data-testid="subtab-offbalance"><Coffee className="h-4 w-4 mr-1.5" /> Off balance</TabsTrigger>}
          </TabsList>

          <TabsContent value="requests">
            <div className="grid lg:grid-cols-2 gap-6">
              {isManager && (
                <div className="space-y-3">
                  <h3 className="font-heading font-semibold text-sm flex items-center gap-2">To review {needsAction.length > 0 && <Badge className="rounded-full border-0 bg-primary text-primary-foreground text-[10px]" data-testid="leave-pending-count">{needsAction.length}</Badge>}</h3>
                  {needsAction.length === 0 && <Card className="p-6 text-center rounded-xl" data-testid="leave-review-empty"><p className="text-sm text-muted-foreground">No requests to review.</p></Card>}
                  <div className="space-y-3" data-testid="leave-review-list">
                    {needsAction.map((r) => (
                      <RequestCard key={r.id} r={r} showRequester
                        actions={<ReviewActions r={r} kind={r.status === "approved" ? "withdrawal" : "decision"} onDone={load} dir={dir} />} />
                    ))}
                  </div>
                  {reviewed.length > 0 && (
                    <>
                      <h4 className="font-heading font-semibold text-xs text-muted-foreground pt-2">History</h4>
                      <div className="space-y-3" data-testid="leave-review-history">
                        {reviewed.map((r) => <RequestCard key={r.id} r={r} showRequester />)}
                      </div>
                    </>
                  )}
                </div>
              )}
              {canRequest && (
                <div className="space-y-3">
                  <h3 className="font-heading font-semibold text-sm">My requests</h3>
                  {data.mine.length === 0 && <Card className="p-6 text-center rounded-xl" data-testid="leave-mine-empty"><p className="text-sm text-muted-foreground">No leave requests yet.</p></Card>}
                  <div className="space-y-3" data-testid="leave-mine-list">
                    {data.mine.map((r) => <RequestCard key={r.id} r={r} actions={<MineActions r={r} onDone={load} />} />)}
                  </div>
                  {(data.viewing || []).length > 0 && (
                    <>
                      <h3 className="font-heading font-semibold text-sm pt-3">Shared with me (view only)</h3>
                      <div className="space-y-3" data-testid="leave-viewing-list">
                        {data.viewing.map((r) => <RequestCard key={r.id} r={r} showRequester />)}
                      </div>
                    </>
                  )}
                </div>
              )}
              {!canRequest && (data.viewing || []).length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-heading font-semibold text-sm">Shared with me (view only)</h3>
                  <div className="space-y-3" data-testid="leave-viewing-list">
                    {data.viewing.map((r) => <RequestCard key={r.id} r={r} showRequester />)}
                  </div>
                </div>
              )}
            </div>
            {(data.team || []).length > 0 && (
              <div className="mt-6 space-y-3">
                <h3 className="font-heading font-semibold text-sm">Team leave (view only)</h3>
                <p className="text-xs text-muted-foreground -mt-1">Leave requests from members assigned to you.</p>
                <div className="grid lg:grid-cols-2 gap-3" data-testid="leave-team-list">
                  {data.team.map((r) => <RequestCard key={r.id} r={r} showRequester />)}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="calendar"><MonthCalendar isManager={isManager} /></TabsContent>
          {canRequest && <TabsContent value="myoff"><NextWeekOff /></TabsContent>}
          {isManager && <TabsContent value="offbalance"><OffBalancePanel /></TabsContent>}
        </Tabs>
      )}

      {canRequest && <CreateLeaveDialog open={creating} onClose={() => setCreating(false)} dir={dir} meId={user.id} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function CreateLeaveDialog({ open, onClose, dir, meId, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [type, setType] = useState("casual");
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [manager, setManager] = useState("");
  const [viewerIds, setViewerIds] = useState([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const managers = dir.filter((p) => p.role === "manager" && p.id !== meId);
  const viewerOptions = managers.filter((m) => m.id !== manager);

  const submit = async () => {
    if (!manager) { toast.error("Please select an approver"); return; }
    setSaving(true);
    try {
      await api.post("/leave", { manager_id: manager, viewer_ids: viewerIds.filter((v) => v !== manager), type, start_date: start, end_date: end, reason });
      toast.success("Leave request submitted");
      setType("casual"); setStart(today); setEnd(today); setManager(""); setViewerIds([]); setReason("");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="create-leave-dialog">
        <DialogHeader><DialogTitle>New leave request</DialogTitle><DialogDescription>Pick your dates, type and the manager to approve it.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <select className="h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" value={type} onChange={(e) => setType(e.target.value)} data-testid="leave-type-select">
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>From</Label><Input type="date" value={start} onChange={(e) => { setStart(e.target.value); if (end < e.target.value) setEnd(e.target.value); }} data-testid="leave-start-input" /></div>
            <div className="space-y-1.5"><Label>To</Label><Input type="date" min={start} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="leave-end-input" /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Approver (manager)</Label>
            <select className="h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" value={manager} onChange={(e) => { setManager(e.target.value); setViewerIds((p) => p.filter((v) => v !== e.target.value)); }} data-testid="leave-approver-select">
              <option value="">— Select a manager —</option>
              {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Viewers (managers who can see it — optional)</Label>
            <div className="max-h-32 overflow-auto rounded-lg border border-border p-2 space-y-1.5" data-testid="leave-viewers">
              {viewerOptions.length === 0 && <p className="text-[11px] text-muted-foreground">No other managers available.</p>}
              {viewerOptions.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={viewerIds.includes(m.id)} onCheckedChange={() => setViewerIds((p) => p.includes(m.id) ? p.filter((x) => x !== m.id) : [...p, m.id])} data-testid={`leave-viewer-${m.id}`} />
                  <span>{m.name}</span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">Dheepak is automatically added as a viewer on every leave.</p>
          </div>
          <div className="space-y-1.5"><Label>Reason</Label><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional reason…" data-testid="leave-reason-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving || !manager} className="rounded-lg" data-testid="submit-leave-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit request"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MonthCalendar({ isManager }) {
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayDetail, setDayDetail] = useState(null);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const { data } = await api.get("/leave-calendar", { params: { month: ym } }); setEntries(data.entries); }
      catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [ym]);
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const startDow = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(y, m, 0).getDate();
  const byDate = {};
  entries.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${ym}-${String(d).padStart(2, "0")}`);
  const shift = (delta) => { const dt = new Date(y, m - 1 + delta, 1); setYm(dt.toISOString().slice(0, 7)); };
  const label = first.toLocaleString("default", { month: "long", year: "numeric" });
  return (
    <Card className="p-4 sm:p-5 rounded-xl" data-testid="leave-calendar">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => shift(-1)} className="p-1.5 rounded-lg hover:bg-secondary" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
        <h3 className="font-heading font-semibold">{label}</h3>
        <button onClick={() => shift(1)} className="p-1.5 rounded-lg hover:bg-secondary" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
        <>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground mb-1">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((c, i) => c === null ? <div key={i} /> : (
              <button key={i} type="button" onClick={() => setDayDetail(c)} className="min-h-[64px] text-left rounded-lg border border-border p-1 hover:border-primary/50 hover:bg-secondary/40 transition-colors" data-testid={`cal-day-${c}`}>
                <div className="text-[11px] text-muted-foreground">{Number(c.slice(-2))}</div>
                <div className="space-y-0.5 mt-0.5">
                  {(byDate[c] || []).slice(0, 3).map((e, j) => (
                    <div key={j} className={`text-[9px] leading-tight rounded px-1 py-0.5 truncate ${e.kind === "leave" ? "bg-sky-500/15 text-sky-700" : "bg-amber-500/15 text-amber-700"}`} title={`${e.name} · ${e.kind}`}>{isManager ? e.name.split(" ")[0] : (e.kind === "leave" ? "Leave" : "Off")}</div>
                  ))}
                  {(byDate[c] || []).length > 3 && <div className="text-[9px] text-primary font-medium">+{(byDate[c] || []).length - 3} more</div>}
                </div>
              </button>
            ))}
          </div>
          <div className="flex gap-4 mt-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-sky-500/40" /> Leave</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-500/40" /> Off</span>
            <span className="ml-auto">Tap a day to see everyone</span>
          </div>
        </>
      )}

      <Dialog open={!!dayDetail} onOpenChange={(o) => !o && setDayDetail(null)}>
        <DialogContent data-testid="cal-day-detail">
          <DialogHeader>
            <DialogTitle>{dayDetail}</DialogTitle>
            <DialogDescription>Everyone on Off or Leave this day{isManager ? "" : " (your entries)"}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-auto">
            {(byDate[dayDetail] || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center" data-testid="cal-day-empty">Nobody is Off or on Leave this day.</p>
            ) : (
              [...(byDate[dayDetail] || [])].sort((a, b) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name)).map((e, i) => (
                <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2" data-testid={`cal-detail-row-${i}`}>
                  <span className="text-sm font-medium truncate">{isManager ? e.name : (e.kind === "leave" ? "Leave" : "Off")}</span>
                  <Badge className={`rounded-full border-0 text-[11px] ${e.kind === "leave" ? "bg-sky-500/15 text-sky-700" : "bg-amber-500/15 text-amber-700"}`}>{e.kind === "leave" ? "Leave" : "Off"}</Badge>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function NextWeekOff({ onChange }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);
  const nm = (() => { const t = new Date(); const d = new Date(t); d.setDate(t.getDate() + (7 - ((t.getDay() + 6) % 7))); return d; })();
  const load = async () => { try { const { data } = await api.get("/my-offs", { params: { week_start: isoDate(nm) } }); setInfo(data); } catch (e) { /* ignore */ } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(nm); d.setDate(nm.getDate() + i); return d; });
  const offDates = info?.off_dates || [];
  const toggle = async (date) => {
    const on = !offDates.includes(date);
    setBusy(date);
    try { await api.post("/my-offs", { date, off: on }); await load(); onChange && onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };
  const wed = new Date(); wed.setDate(wed.getDate() - ((wed.getDay() + 4) % 7));
  return (
    <div data-testid="next-week-off">
      <div className="flex items-center gap-2 mb-1"><Coffee className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold">Mark next week's Off</h3></div>
      <p className="text-sm text-muted-foreground mb-1">{info ? `${info.next_week_start} to ${info.next_week_end}` : ""} · up to 2 Off days</p>
      <p className="text-xs text-amber-600 mb-4 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Set on or before Wednesday — otherwise your manager & Dheepak are notified.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {days.map((d) => {
          const ds = isoDate(d);
          const on = offDates.includes(ds);
          return (
            <button key={ds} onClick={() => toggle(ds)} disabled={busy === ds}
              className={`rounded-xl border p-3 text-left transition-colors ${on ? "border-amber-500 bg-amber-500/10" : "border-border hover:border-primary/40"}`}
              data-testid={`nwoff-${ds}`}>
              <div className="text-[11px] text-muted-foreground">{d.toLocaleDateString("default", { weekday: "short" })}</div>
              <div className="font-heading font-semibold">{d.getDate()}/{d.getMonth() + 1}</div>
              <div className={`text-[11px] mt-1 font-medium ${on ? "text-amber-600" : "text-muted-foreground"}`}>{on ? "Off ✓" : "Tap to mark"}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OffBalancePanel() {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { try { const res = await api.get("/off-balance"); setData(res.data); } catch (e) { /* ignore */ } })(); }, []);
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  const tone = (n) => n === 0 ? "bg-destructive/10 text-destructive" : n >= 2 ? "bg-amber-500/15 text-amber-700" : "bg-emerald-500/15 text-emerald-700";
  return (
    <div data-testid="offbal-panel">
      <p className="text-sm text-muted-foreground mb-4">{data.week_start} to {data.week_end} · everyone should take 1–2 Off days</p>
      {data.rows.length === 0 ? <Card className="p-8 text-center rounded-xl"><p className="text-sm text-muted-foreground">No team members to show.</p></Card> : (
        <div className="space-y-2" data-testid="offbal-list">
          {data.rows.map((r) => (
            <Card key={r.user_id} className="p-4 rounded-xl flex items-center gap-3" data-testid={`offbal-row-${r.user_id}`}>
              <div className="flex-1 min-w-0"><p className="font-semibold truncate">{r.name}</p>{r.leave_used > 0 && <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5"><Plane className="h-3 w-3" /> {r.leave_used} leave</p>}</div>
              <div className={`shrink-0 rounded-lg px-3 py-1.5 text-sm font-semibold flex items-center gap-1.5 ${tone(r.off_used)}`} data-testid={`offbal-count-${r.user_id}`}><Coffee className="h-4 w-4" /> {r.off_used}/{data.max_off}</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
